import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { REGISTER } from "@/constants/testIds";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", role: "sales" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirm) return toast.error("Passwords do not match");
        setBusy(true);
        const res = await register({ name: form.name, email: form.email, password: form.password, role: form.role });
        setBusy(false);
        if (res.ok) { toast.success("Welcome to Zyberzing"); nav("/"); }
        else toast.error(res.error || "Registration failed");
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                        <Zap className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div className="font-heading text-2xl font-bold tracking-tight">Zyberzing</div>
                </div>
                <h2 className="font-heading text-3xl font-bold tracking-tight">Create your account</h2>
                <p className="text-sm text-muted-foreground mt-1">Set up your workspace in seconds.</p>
                <form onSubmit={submit} className="mt-8 space-y-4">
                    {[
                        { key: "name", label: "Full Name", type: "text", tid: REGISTER.nameInput },
                        { key: "email", label: "Email", type: "email", tid: REGISTER.emailInput },
                        { key: "password", label: "Password", type: "password", tid: REGISTER.passwordInput },
                        { key: "confirm", label: "Confirm Password", type: "password", tid: REGISTER.passwordConfirmInput },
                    ].map((f) => (
                        <div key={f.key} className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{f.label}</label>
                            <input
                                type={f.type}
                                required
                                data-testid={f.tid}
                                value={form[f.key]}
                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                className="w-full h-11 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                            />
                        </div>
                    ))}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Role</label>
                        <select
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="w-full h-11 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                        >
                            <option value="sales">Sales</option>
                            <option value="project_manager">Project Manager</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid={REGISTER.submitButton}
                        className="w-full h-11 rounded-md bg-primary text-primary-foreground font-medium text-sm inline-flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.98] transition-colors transition-transform disabled:opacity-60"
                    >
                        {busy ? "Creating…" : (<>Create account <ArrowRight className="h-4 w-4" /></>)}
                    </button>
                </form>
                <div className="mt-6 text-sm text-muted-foreground">
                    Already have an account? <Link to="/login" data-testid={REGISTER.loginLink} className="text-primary font-medium hover:underline">Sign in</Link>
                </div>
            </div>
        </div>
    );
}
